// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// How fast a spectator's re-simulation runs (M8-19).
//
// The re-simulation itself needs no help: the same core, the same seed and the same actions
// produce the same steps, so a spectator's picture is **frame-exact** rather than interpolated.
// The only open question is *when* to run each step, and that is this file.
//
// **A spectator plays deliberately behind, for the same reason the pose stream draws behind**
// (`poseStream.RENDER_DELAY_MS`). Packets arrive a quarter of a second apart, so a viewer running
// right at the head of what it has reaches the end of the trace between every pair of them and
// stalls — sixty frames of ball, then a freeze, then sixty more. Holding a fraction of a second
// back means there is almost always more trace in hand than there is time to play it, so **one
// lost packet is a smaller buffer rather than a visible stutter**.
//
// **Only the accelerator is written here, because the trace's own end is the brake.** `ghostFrame`
// already clamps to the steps it has, so a viewer that has caught up simply stops advancing; there
// is no "too fast" to correct for and nothing to write to prevent it.
//
// No clock: the two numbers are step counts, so a test names the situation instead of waiting for
// one.

/** How far behind the live machine a spectator aims to be, in steps — 400 ms. */
export const SPECTATOR_DELAY_STEPS = 400

/**
 * How much faster than real time a spectator plays while catching up.
 *
 * 1.2. **A ball moving twenty per cent fast is almost impossible to see and two times real is
 * impossible to miss**, and there is no hurry: a fifth faster recovers a second of lag in five
 * seconds, which is well inside a ball. Catching up should be something a viewer never notices
 * happening rather than something they watch.
 */
export const CATCH_UP_SPEED = 1.2

/**
 * Lag past which a spectator skips forward instead of hurrying.
 *
 * Five seconds. Past this, catching up at 1.2× would take half a minute of watching a ball that
 * has already happened — and a spectator asked to watch the *live* ball. The step is thrown away
 * rather than shown fast, which is the same decision a scrub makes: land there, and do not fire
 * the intervening minute of callouts on arrival (`seekGhost`).
 */
export const SEEK_BEHIND_STEPS = 5_000

/** Lag that starts a hurry. Twice the target, so the pace does not flicker around the boundary. */
export const CATCH_UP_AFTER_STEPS = SPECTATOR_DELAY_STEPS * 2

/** What the next frame of a spectator's playback should do. */
export type SpectatorPlan =
  /** Jump, then carry on at this speed. Everything skipped is discarded, not played fast. */
  | { readonly kind: 'seek'; readonly to: number; readonly speed: number }
  | { readonly kind: 'play'; readonly speed: number }

/**
 * Decide the next frame's pace from where the playback is and where the player is.
 *
 * `liveSteps` is what the player's own machine has run, as their last packet reported it — so it
 * is itself a fraction of a second old, and the target below is measured against it rather than
 * against anything this client could know more precisely.
 */
export function spectatorPlan(step: number, liveSteps: number): SpectatorPlan {
  const behind = liveSteps - step
  if (behind > SEEK_BEHIND_STEPS) {
    return { kind: 'seek', to: Math.max(0, liveSteps - SPECTATOR_DELAY_STEPS), speed: 1 }
  }
  if (behind > CATCH_UP_AFTER_STEPS) return { kind: 'play', speed: CATCH_UP_SPEED }
  return { kind: 'play', speed: 1 }
}
